import { FC, UIEvent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiFileText } from 'react-icons/fi';

import { getMeetingDescription } from '../utils/meeting-description';

export const MeetingDescriptionCard: FC<{ description?: string | null }> = ({ description }) => {
  const { t } = useTranslation();
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const text = getMeetingDescription(description);

  const measure = useCallback((element: HTMLDivElement) => {
    const overflow = element.scrollHeight - element.clientHeight;
    setHasMoreBelow(overflow - element.scrollTop > 1);

    // Only a scrollable box needs to be focusable, so keyboard users can scroll it (WCAG 2.1.1);
    // set outside JSX so a description that fits does not become a pointless tab stop.
    if (overflow > 1) element.setAttribute('tabindex', '0');
    else element.removeAttribute('tabindex');
  }, []);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    measure(element);
    const observer = new ResizeObserver(() => measure(element));
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, text]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => measure(event.currentTarget);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
        <FiFileText size={18} className="text-gray-500 flex-shrink-0" />
        {t('meeting-secretary.meetings.description-title')}
      </h2>

      {text ? (
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            role="region"
            aria-label={t('meeting-secretary.meetings.description-title')}
            className="thin-scrollbar max-h-64 overflow-y-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <p className="text-gray-700 whitespace-pre-wrap break-words">{text}</p>
          </div>
          {hasMoreBelow && (
            <div
              aria-hidden
              data-testid="description-more-below"
              className="pointer-events-none absolute bottom-0 left-0 right-2 h-8 bg-gradient-to-b from-transparent to-white"
            />
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{t('meeting-secretary.meetings.description-empty')}</p>
      )}
    </div>
  );
};
